import { evaluateBirthDate } from "@usas/ui-web/utils/validation";
import { MINIMUM_ACCOUNT_AGE } from "./constants";

/**
 * Determines if a user is under the minimum account age based on birth date
 * @param {Date} birthDate - The user's birth date
 * @returns {boolean} - True if user is under minimum age
 */
export const isUserUnderMinimumAge = (birthDate) => {
  if (!birthDate) return false;
  
  const birthDateEvaluation = evaluateBirthDate(birthDate);
  return birthDateEvaluation?.ageInYears < MINIMUM_ACCOUNT_AGE;
};

/**
 * Safely compares two email addresses (case insensitive)
 * @param {string} existingEmail - First email to compare
 * @param {string} enteredEmail - Second email to compare  
 * @returns {boolean} - True if emails match
 */
export const doEmailsMatch = (existingEmail, enteredEmail) => {
  try {
    return existingEmail.toLowerCase() === enteredEmail.toLowerCase();
  } catch (e) {
    return false;
  }
};

/**
 * Merges user info data with birth date from account creation state
 * @param {Object} accountCreationState - The account creation state
 * @returns {Object} - Merged user info data
 */
export const getUserInfoDataWithBirthDate = (accountCreationState) => {
  const userInfoData = accountCreationState?.userInfoData || {};
  const birthDate = accountCreationState.confirmAgeData?.birthDate;
  
  return {
    ...userInfoData,
    ...(birthDate && { birthDate })
  };
};

/**
 * Prepares data for the CreateStep1 API call
 * @param {Object} userInfoData - User information data
 * @param {Object} accountCreationState - Current account creation state
 * @returns {Object} - Formatted data for CreateStep1 API
 */
export const prepareCreateStep1Data = (userInfoData, accountCreationState) => {
  const youthContactData = accountCreationState.youthContactData || {};
  const parentVerificationData = accountCreationState.parentVerificationData || {};
  
  const createStep1Data = {
    ...userInfoData,
    emailAddress: userInfoData.email,
    email: undefined // Remove the original email field
  };

  // Add parent verification ID if available
  if (userInfoData.parentVerificationId) {
    createStep1Data.parentVerificationId = userInfoData.parentVerificationId;
  } else if (parentVerificationData.parentVerificationId) {
    createStep1Data.parentVerificationId = parentVerificationData.parentVerificationId;
  }

  // Add parent phone if available
  if (accountCreationState.parentPhoneData?.parentPhone) {
    createStep1Data.parentPhone = accountCreationState.parentPhoneData.parentPhone;
  } else if (userInfoData.phoneNumber && isUserUnderMinimumAge(userInfoData.birthDate)) {
    createStep1Data.parentPhone = userInfoData.phoneNumber;
  }

  // Add youth contact information if provided
  if (youthContactData.youthEmail) {
    createStep1Data.youthEmail = youthContactData.youthEmail;
  }
  if (youthContactData.youthPhoneNumber) {
    createStep1Data.youthPhoneNumber = youthContactData.youthPhoneNumber;
  }

  // Add personId for linking if available (from PersonDupPopupManager selection)
  if (userInfoData.linkPersonId) {
    createStep1Data.personId = userInfoData.linkPersonId;
  }

  return createStep1Data;
};